import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { NotificationTypesActions } from "@/components/common/section-components/action/NotificationTypesActions";
import { StandardTable } from "@/components/table/StandardTable";
import { CodigosNotificacion } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import type { NotificationTypeListItem } from "@/services/notification-types.service";
import { AppColors } from "@/styles/colors";
import { Flex, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchNotificationTypesHandler } from "./schema/handler-functions";
import { notificationTypesHeaderElements } from "./schema/header-elements";

type NotificationSectionKey = "finance" | "grain" | "supplies" | "profile" | "others";

type NotificationSection = {
  key: Exclude<NotificationSectionKey, "others">;
  title: string;
  orderedCodes: string[];
};

const NOTIFICATION_SECTIONS: NotificationSection[] = [
  {
    key: "finance",
    title: "FINANZAS",
    orderedCodes: [
      CodigosNotificacion.FinancieraEfectivoDisponible,
      CodigosNotificacion.FinancieraEfectivoInfoSolicitada,
      CodigosNotificacion.FinancieraChequeDisponible,
      CodigosNotificacion.FinancieraChequeInfoSolicitada,
      CodigosNotificacion.FinancieraTransferenciaProgramadaEjecutada,
      CodigosNotificacion.FinancieraTransferenciaInternaRecibida,
      CodigosNotificacion.FinancieraSaldoProyectadoNegativo,
      CodigosNotificacion.FinancieraFacturasVencidas,
    ],
  },
  {
    key: "grain",
    title: "GRANARIA",
    orderedCodes: [
      CodigosNotificacion.GranarioOrdenVentaConfirmada,
      CodigosNotificacion.GranarioOrdenVentaEnNegociacion,
      CodigosNotificacion.GranarioContraEntregaConfirmada,
      CodigosNotificacion.GranarioContraEntregaEnNegociacion,
      CodigosNotificacion.GranarioVentaFuturaConfirmada,
      CodigosNotificacion.GranarioVentaFuturaEnNegociacion,
      CodigosNotificacion.GranarioCupoAsignado,
      CodigosNotificacion.GranarioUltimasEntregas,
      CodigosNotificacion.GranarioDescargasTransito,
    ],
  },
  {
    key: "supplies",
    title: "INSUMOS",
    orderedCodes: [CodigosNotificacion.InsumosPedidoEntregado],
  },
  {
    key: "profile",
    title: "SECCIONES PERFIL",
    orderedCodes: [
      CodigosNotificacion.FinancieraFondosACvu,
      CodigosNotificacion.FinancieraCvuDesasignado,
      CodigosNotificacion.OnboardingPjInfoSolicitada,
      CodigosNotificacion.OnboardingPjAprobada,
    ],
  },
];

const buildCodeIndexMap = (sections: NotificationSection[]) => {
  const map = new Map<string, { sectionKey: NotificationSectionKey; index: number }>();

  for (const section of sections) {
    section.orderedCodes.forEach((code, index) => {
      if (!map.has(code)) map.set(code, { sectionKey: section.key, index });
    });
  }

  return map;
};

export const NotificationTypesPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<NotificationTypeListItem[] | null>(null);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const { fetchPermissions } = useRoles();

  const fetchList = async (showLoad = true) => {
    await fetchNotificationTypesHandler({
      setLoading,
      setItems,
      navigate,
      fetchPermissions,
      showLoad,
      routeState,
    });
  };

  useEffect(() => {
    if (!isActive) return;
    fetchList(true);
  }, [isActive]);

  const codeIndexBySection = useMemo(
    () => buildCodeIndexMap(NOTIFICATION_SECTIONS),
    [],
  );

  const grouped = useMemo(() => {
    const groups: Record<NotificationSectionKey, NotificationTypeListItem[]> = {
      finance: [],
      grain: [],
      supplies: [],
      profile: [],
      others: [],
    };

    for (const item of items ?? []) {
      const codeInfo = codeIndexBySection.get(item.codigo);
      if (!codeInfo) {
        groups.others.push(item);
        continue;
      }

      groups[codeInfo.sectionKey].push(item);
    }

    const sortByCatalog = (a: NotificationTypeListItem, b: NotificationTypeListItem) => {
      const ai = codeIndexBySection.get(a.codigo)?.index ?? Number.MAX_SAFE_INTEGER;
      const bi = codeIndexBySection.get(b.codigo)?.index ?? Number.MAX_SAFE_INTEGER;
      return ai - bi;
    };

    (Object.keys(groups) as NotificationSectionKey[]).forEach((k) => {
      groups[k] = groups[k].slice().sort(sortByCatalog);
    });

    return groups;
  }, [items, codeIndexBySection]);

  const handleView = (item: NotificationTypeListItem) => {
    navigate(AppRoutes.NOTIFICATION_DETAIL.replace(":id", String(item.id)), {
      state: { item },
    });
  };

  const getActionElements = (item: NotificationTypeListItem) => {
    return <NotificationTypesActions item={item} handleView={handleView} />;
  };

  const sectionsToRender = useMemo(() => {
    const base: {
      key: NotificationSectionKey;
      title: string;
      items: NotificationTypeListItem[];
    }[] = NOTIFICATION_SECTIONS.map((s) => ({
      key: s.key,
      title: s.title,
      items: grouped[s.key],
    }));

    if (grouped.others.length) {
      base.push({
        key: "others",
        title: "OTROS",
        items: grouped.others,
      });
    }

    return base;
  }, [grouped]);

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="24px"
      data-testid="notification-types-page"
      height="100%"
      minH={0}
    >
      <SectionHeader title="Notificaciones" loading={loading} />

      <Flex flexDir="column" paddingBottom="16px" gap="18px" overflowY="auto" flex={1} minH={0}>
        {Boolean(!loading && !(items ?? []).length) && <NoData />}

        {Boolean(!loading && (items ?? []).length) && (
          <Flex flexDir="column" gap="18px">
            {sectionsToRender.map((section) => (
              <Flex key={section.key} flexDir="column" gap="10px">
                <Text fontWeight={700} fontSize="14px" color={AppColors.GRAY_TEXT}>
                  {section.title}
                </Text>

                <Flex
                  width="100%"
                  css={{
                    "& table": {
                      tableLayout: "fixed",
                      width: "100%",
                    },
                    "& th:nth-child(1)": {
                      width: "18%",
                    },
                    "& th:nth-child(2)": {
                      width: "18%",
                    },
                    "& th:nth-child(3)": {
                      width: "54%",
                    },
                    "& th:nth-child(4)": {
                      width: "10%",
                    },
                  }}
                >
                  <StandardTable
                    headers={notificationTypesHeaderElements}
                    items={section.items}
                    noSelection
                    actionElements={getActionElements}
                    actionsHeaderLabel="Ver"
                    actionsAlign="center"
                    headerLabelAlign="center"
                    fullWidth
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
